import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzMessageService } from 'ng-zorro-antd/message';
import { RH_ACTIONS } from '@shared/constants/rh-actions.constants';
import { VacacionesService } from '@services/vacaciones.service';
import { SolicitudVacaciones } from '@schemas/vacaciones.schema';
import { getEstatusColor as _getEstatusColor } from '@shared/vacaciones.helpers';
import { SolicitudDetalleModal } from '../../../components/solicitud-detalle-modal/solicitud-detalle-modal';
import { SolicitudEditarAdminModal } from '../../../components/solicitud-editar-admin-modal/solicitud-editar-admin-modal';

import {
  CellStack,
  PageHeader,
  StatChip,
  TableActions,
  type ActionButtonDef,
  DataTable,
  CellTemplateDirective,
  type ColumnDef,
  type TableAction,
} from '@intelishs-group/ui';

@Component({
  selector: 'app-vacaciones-lista',
  imports: [
    CommonModule,
    NzModalModule,
    NzDividerModule,
    DataTable,
    CellTemplateDirective,
    TableActions,
    CellStack,
    PageHeader,
    StatChip,
    SolicitudDetalleModal,
    SolicitudEditarAdminModal,
  ],
  templateUrl: './vacaciones-lista.page.html',
  styleUrl: './vacaciones-lista.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VacacionesListaPage implements OnInit {
  private readonly vacacionesService = inject(VacacionesService);
  private readonly message = inject(NzMessageService);
  private readonly destroyRef = inject(DestroyRef);

  solicitudes = signal<SolicitudVacaciones[]>([]);
  solicitudesFiltradas = signal<SolicitudVacaciones[]>([]);
  loading = signal(false);
  filtroEstatus = '';

  /**
   * Antes eran 10 columnas sumando 1440px: la tabla vivía con scroll horizontal
   * y terminaba con el estatus DESPUÉS de la botonera. Ahora son 6 (~1030px),
   * con los dos extremos anclados: el estatus a la izquierda porque es la
   * referencia con la que el usuario se ubica (y por la que filtra con los
   * chips de arriba), y las acciones a la derecha para que sigan alcanzables
   * en pantallas angostas.
   */
  columns: ColumnDef<SolicitudVacaciones>[] = [
    {
      key: 'estatus',
      header: 'Estatus',
      // 115px: "Aprobada Director" (17 chars) a 11px mide ~100px + padding.
      width: '115px',
      align: 'center',
      type: 'tag',
      tagColorFn: (v) => _getEstatusColor(v),
    },
    // Nombre sobre "MC#### · puesto". El puesto existe en el schema y hasta
    // ahora no se mostraba en ninguna columna.
    {
      key: 'empleadoNombre',
      header: 'Empleado',
      width: 'auto',
      type: 'custom',
      // La celda es un `cell-stack`, y sus dos lineas no truncan solas: el
      // `nowrap` evita que la fila crezca de alto, pero sin el flag el valor
      // largo ensancha la columna y desborda la tabla.
      ellipsis: true,
      filterable: true,
      sortable: true,
    },
    {
      key: 'area',
      header: 'Área / Dirección',
      width: 'auto',
      type: 'custom',
      ellipsis: true,
      filterable: true,
    },
    // Fecha inicio y fin son un rango: se leen juntas, no en dos columnas. Los
    // días hábiles bajan a la línea de contexto.
    {
      key: 'fechaInicio',
      header: 'Periodo',
      width: '200px',
      type: 'custom',
      ellipsis: true,
      sortable: true,
    },
    {
      key: 'fechaSolicitud',
      header: 'Solicitada',
      width: '115px',
      type: 'date',
      sortable: true,
    },
    {
      key: 'acciones',
      header: 'Acciones',
      width: '90px',
      align: 'center',
      type: 'custom',
    },
  ];

  /** Buscables sin columna propia (viven en la línea meta de otra celda). */
  readonly extraFilterKeys = ['empleadoIdInterno', 'puesto', 'direccion'];

  /** El Excel conserva el desglose completo aunque la tabla se compacte. */
  readonly exportColumns: ColumnDef<SolicitudVacaciones>[] = [
    { key: 'empleadoIdInterno', header: 'ID Interno' },
    { key: 'empleadoNombre', header: 'Empleado' },
    { key: 'puesto', header: 'Puesto' },
    { key: 'area', header: 'Área' },
    { key: 'direccion', header: 'Dirección' },
    { key: 'fechaInicio', header: 'Fecha Inicio', type: 'date' },
    { key: 'fechaFin', header: 'Fecha Fin', type: 'date' },
    { key: 'diasSolicitados', header: 'Días' },
  ];

  readonly tableActions: ActionButtonDef[] = [RH_ACTIONS.ver, RH_ACTIONS.editar];

  onAction(event: TableAction<SolicitudVacaciones>): void {
    switch (event.action) {
      case 'ver':
        this.abrirDetalle(event.row);
        break;
      case 'editar':
        this.abrirEditar(event.row);
        break;
    }
  }

  // Modal detalle — el modal carga el saldo internamente
  detalleVisible = signal(false);
  detalleSolicitud = signal<SolicitudVacaciones | null>(null);

  // Modal edición administrativa — el modal gestiona su propio estado
  editVisible = signal(false);
  editSolicitud = signal<SolicitudVacaciones | null>(null);

  totalSolicitudes = computed(() => this.solicitudes().length);
  totalPendientes = computed(
    () => this.solicitudes().filter((s) => s.estatus === 'Pendiente').length,
  );
  totalAprobadaDir = computed(
    () => this.solicitudes().filter((s) => s.estatus === 'Aprobada Director').length,
  );
  totalAprobadas = computed(
    () => this.solicitudes().filter((s) => s.estatus === 'Aprobada').length,
  );
  totalRechazadasDir = computed(
    () => this.solicitudes().filter((s) => s.estatus === 'Rechazada Director').length,
  );
  totalRechazadas = computed(
    () => this.solicitudes().filter((s) => s.estatus === 'Rechazada').length,
  );
  totalCanceladas = computed(
    () => this.solicitudes().filter((s) => s.estatus === 'Cancelada').length,
  );

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    this.loading.set(true);
    this.vacacionesService
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.solicitudes.set(data);
          this.buscar();
          this.loading.set(false);
        },
        error: () => {
          this.message.error('Error al cargar solicitudes');
          this.loading.set(false);
        },
      });
  }

  /**
   * Las solicitudes cerradas sin aprobarse se pintan con el color de su pill de
   * estatus (`ESTATUS_COLORES`), que es lo que la fila complementa: rojo las
   * rechazadas —por el director o por RH— y gris las canceladas.
   *
   * Rojo es `.sinay-fila-critica` del theme. El gris no existe en el theme como
   * estado de fila (sus neutros son de profundidad, `-nivel-1/2`), así que
   * `fila-cancelada` es una prueba local en el CSS de esta página.
   *
   * Va por `rowClassFn` porque el `<tr>` lo renderiza el data-table y el CSS
   * encapsulado de la página no lo alcanza.
   */
  filaClase = (row: SolicitudVacaciones) => ({
    'sinay-fila-critica': row.estatus === 'Rechazada' || row.estatus === 'Rechazada Director',
    'fila-cancelada': row.estatus === 'Cancelada',
  });

  /** «Total» quita el filtro. */
  verTodas(): void {
    this.filtroEstatus = '';
    this.buscar();
  }

  /**
   * Cada chip enciende y apaga su filtro: volver a pulsar el activo lo quita,
   * como en el listado de empleados. Antes había que ir a «Total».
   */
  alternarEstatus(estatus: string): void {
    this.filtroEstatus = this.filtroEstatus === estatus ? '' : estatus;
    this.buscar();
  }

  buscar(): void {
    let resultado = this.solicitudes();

    if (this.filtroEstatus) {
      resultado = resultado.filter((s) => s.estatus === this.filtroEstatus);
    }

    // No re-ordenar aquí: el backend ya devuelve la lista en el orden correcto
    // (Pendiente y Aprobada Director arriba, luego el resto; dentro de cada
    // grupo FechaSolicitud DESC). Array.filter() preserva el orden, así que
    // las solicitudes filtradas siguen viniendo bien. Si el usuario hace
    // click en un header de columna, ng-zorro re-sortea client-side a partir
    // de ese orden — comportamiento esperado.
    this.solicitudesFiltradas.set(resultado);
  }

  abrirDetalle(sol: SolicitudVacaciones): void {
    this.detalleSolicitud.set(sol);
    this.detalleVisible.set(true);
  }

  abrirEditar(sol: SolicitudVacaciones): void {
    this.editSolicitud.set(sol);
    this.editVisible.set(true);
  }

  onEditarGuardado(): void {
    this.cargar();
  }

  getEstatusColor = _getEstatusColor;
}
